// Forecast stacks: named bundles of forecast cards in the Forecast Manager, so a user can
// group the forecasts behind one decision. Pure: every edit returns a new stacks array, saved
// by the caller (users.preferences.forecastStacks).
//
// A stack is { id, name, cardIds } — cardIds in display order, a card in at most one stack. A
// stack needs two cards: when only one is left, the stack goes and that card is a plain card
// again.

export const DEFAULT_STACK_NAME = "Untitled stack";
export const STACK_NAME_MAX = 80;
/** The server's limits (server/lib/forecastStacksPref.js): edits past them are refused here. */
export const MAX_STACKS = 200;
export const MAX_CARDS_PER_STACK = 200;
/** The fewest cards a stack holds; below this it dissolves. */
export const MIN_CARDS_PER_STACK = 2;

/**
 * Keep only well-formed stacks: string ids, a name, string card ids, no card in two stacks,
 * no stack of fewer than two. Whatever was saved, the UI only ever sees this shape.
 *
 * @param {unknown} raw
 * @returns {Array<{id: string, name: string, cardIds: string[]}>}
 */
export function sanitizeStacks(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const s of raw) {
    if (!s || typeof s.id !== "string" || !Array.isArray(s.cardIds)) continue;
    if (out.some((o) => o.id === s.id)) continue;
    const cardIds = [];
    for (const c of s.cardIds) {
      if (typeof c !== "string" || seen.has(c) || cardIds.includes(c)) continue;
      cardIds.push(c);
    }
    if (cardIds.length < MIN_CARDS_PER_STACK) continue;
    // Only a stack that is kept claims its cards, so a dropped one can't take a card from a
    // later stack.
    cardIds.forEach((c) => seen.add(c));
    const name = typeof s.name === "string" && s.name.trim() ? s.name.trim() : DEFAULT_STACK_NAME;
    out.push({ id: s.id, name: name.slice(0, STACK_NAME_MAX), cardIds });
  }
  return out;
}

/** The stack holding `cardId`, or null. */
export function stackOf(stacks, cardId) {
  return stacks.find((s) => s.cardIds.includes(cardId)) ?? null;
}

const withoutCard = (stacks, cardId) =>
  stacks
    .map((s) =>
      s.cardIds.includes(cardId) ? { ...s, cardIds: s.cardIds.filter((c) => c !== cardId) } : s,
    )
    .filter((s) => s.cardIds.length >= MIN_CARDS_PER_STACK);

/**
 * Drop `cardId` onto `targetId`: into the target's stack if it has one, else a new stack of
 * the two (the target first). The card leaves any stack it was in.
 *
 * @param {Array<{id: string, name: string, cardIds: string[]}>} stacks
 * @param {string} cardId
 * @param {string} targetId
 * @param {string} newId id for a new stack, if one is made
 * @returns {{stacks: Array<{id: string, name: string, cardIds: string[]}>, createdId: string|null}}
 */
export function stackCards(stacks, cardId, targetId, newId) {
  if (!cardId || !targetId || cardId === targetId) return { stacks, createdId: null };
  const rest = withoutCard(stacks, cardId);
  const target = stackOf(rest, targetId);
  // Past the server's limits the edit would be refused on save, so it is refused here.
  if (target ? target.cardIds.length >= MAX_CARDS_PER_STACK : rest.length >= MAX_STACKS) {
    return { stacks, createdId: null };
  }
  if (target) {
    return {
      stacks: rest.map((s) => (s === target ? { ...s, cardIds: [...s.cardIds, cardId] } : s)),
      createdId: null,
    };
  }
  return {
    stacks: [...rest, { id: newId, name: DEFAULT_STACK_NAME, cardIds: [targetId, cardId] }],
    createdId: newId,
  };
}

/** Take `cardId` out of its stack; a stack left with one card dissolves. */
export function unstackCard(stacks, cardId) {
  return withoutCard(stacks, cardId);
}

/** Rename a stack; a blank name keeps the old one. */
export function renameStack(stacks, stackId, name) {
  const next = typeof name === "string" ? name.trim().slice(0, STACK_NAME_MAX) : "";
  if (!next) return stacks;
  return stacks.map((s) => (s.id === stackId ? { ...s, name: next } : s));
}

/** Move `fromId` to `toId`'s place inside one stack (after it moving down, before it moving up). */
export function reorderInStack(stacks, stackId, fromId, toId) {
  return stacks.map((s) => {
    if (s.id !== stackId) return s;
    const from = s.cardIds.indexOf(fromId);
    const to = s.cardIds.indexOf(toId);
    if (from < 0 || to < 0 || from === to) return s;
    const next = s.cardIds.filter((c) => c !== fromId);
    const anchor = next.indexOf(toId);
    next.splice(from < to ? anchor + 1 : anchor, 0, fromId);
    return { ...s, cardIds: next };
  });
}

/**
 * Make `cardId` the first card of its stack, so it leads the stack. Returns the same array when
 * the card is in no stack or already leads it.
 *
 * @param {Array<{id: string, name: string, cardIds: string[]}>} stacks
 * @param {string} cardId
 * @returns {Array<{id: string, name: string, cardIds: string[]}>}
 */
export function promoteInStack(stacks, cardId) {
  const stack = stackOf(stacks, cardId);
  if (!stack || stack.cardIds[0] === cardId) return stacks;
  return reorderInStack(stacks, stack.id, cardId, stack.cardIds[0]);
}

/**
 * Lay the two lists out with their stacks folded in. A stack sits in Watchlist when any of its
 * cards is watched, else in Recent, and takes the place of its "lead" card: its first card in
 * stack order that belongs to that list. Its cards then leave the plain lists.
 *
 * @template F
 * @param {{watchlist: F[], recent: F[]}} lists ordered cards (`F` has an `id`)
 * @param {Array<{id: string, name: string, cardIds: string[]}>} stacks
 * @returns {{watchlist: Array<{kind: "card", f: F} | {kind: "stack", stack: object, lead: F, cards: F[]}>,
 *   recent: Array<{kind: "card", f: F} | {kind: "stack", stack: object, lead: F, cards: F[]}>}}
 */
export function layoutWithStacks(lists, stacks) {
  const byId = new Map([...lists.watchlist, ...lists.recent].map((f) => [f.id, f]));
  const watchedIds = new Set(lists.watchlist.map((f) => f.id));
  const placed = new Map();
  for (const s of stacks) {
    const cards = s.cardIds.map((id) => byId.get(id)).filter(Boolean);
    if (cards.length < MIN_CARDS_PER_STACK) continue;
    const section = cards.some((f) => watchedIds.has(f.id)) ? "watchlist" : "recent";
    const lead = cards.find((f) => (section === "watchlist") === watchedIds.has(f.id));
    placed.set(s.id, { section, item: { kind: "stack", stack: s, lead, cards } });
  }
  const stackByCard = new Map();
  for (const [id, p] of placed) for (const f of p.item.cards) stackByCard.set(f.id, placed.get(id));
  const build = (section) =>
    lists[section].flatMap((f) => {
      const p = stackByCard.get(f.id);
      if (!p) return [{ kind: "card", f }];
      return p.section === section && p.item.lead.id === f.id ? [p.item] : [];
    });
  return { watchlist: build("watchlist"), recent: build("recent") };
}

/**
 * Move `cardId` into stack `stackId`, just before `beforeId` (or at the end). The card leaves
 * any stack it was in.
 *
 * @param {Array<{id: string, name: string, cardIds: string[]}>} stacks
 * @param {string} cardId
 * @param {string} stackId
 * @param {string|null} beforeId
 */
export function moveIntoStack(stacks, cardId, stackId, beforeId) {
  const dest = stacks.find((s) => s.id === stackId);
  if (!dest || (!dest.cardIds.includes(cardId) && dest.cardIds.length >= MAX_CARDS_PER_STACK)) {
    return stacks;
  }
  // Leave the old stack first, but never dissolve the destination on the way.
  const rest = stacks
    .map((s) =>
      s.cardIds.includes(cardId) ? { ...s, cardIds: s.cardIds.filter((c) => c !== cardId) } : s,
    )
    .filter((s) => s.cardIds.length >= MIN_CARDS_PER_STACK || s.id === stackId);
  return rest.map((s) => {
    if (s.id !== stackId) return s;
    const ids = [...s.cardIds];
    const at = beforeId ? ids.indexOf(beforeId) : -1;
    ids.splice(at >= 0 ? at : ids.length, 0, cardId);
    return { ...s, cardIds: ids };
  });
}

const THEME_STOP = new Set(
  (
    "will would could should does did do is are was were be been being has have had the a an " +
    "of in on at to for by with from as and or than then that this these those its it their " +
    "what which who whom whose when where why how any all more most less least over under " +
    "above below before after during between into about against per vs via end start next " +
    "first last new year years month months day days week weeks q1 q2 q3 q4 one two three " +
    "least most highest lowest top number total announce announces reach reaches exceed " +
    "exceeds rise rises fall falls percent level levels rate"
  ).split(" "),
);

/**
 * A quick 1-3 word theme for a stack, from the words its questions share — the name shown at
 * once while a better suggestion is fetched. Null when the questions share nothing useful.
 *
 * @param {string[]} questions
 * @returns {string|null}
 */
export function stackTheme(questions) {
  const qs = questions.filter((q) => typeof q === "string" && q.trim());
  if (qs.length < 2) return null;
  const counts = new Map();
  const shown = new Map();
  for (const q of qs) {
    const words = (q.match(/[A-Za-z][A-Za-z&.'-]*/g) || [])
      .map((w) => w.replace(/[.']+$/, ""))
      .filter((w) => w.length > 1 && !THEME_STOP.has(w.toLowerCase()));
    // Each word counts once per question, whatever its case ("Fed" and "fed" are one word).
    const counted = new Set();
    for (const w of words) {
      const k = w.toLowerCase();
      if (!counted.has(k)) {
        counted.add(k);
        counts.set(k, (counts.get(k) || 0) + 1);
      }
      // Keep the most capitalized spelling seen ("FOMC" over "fomc").
      const prev = shown.get(k);
      if (!prev || (w.match(/[A-Z]/g) || []).length > (prev.match(/[A-Z]/g) || []).length)
        shown.set(k, w);
    }
  }
  const shared = [...counts.entries()]
    .filter(([, n]) => n >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([k]) => shown.get(k))
    .map((w) => (w === w.toLowerCase() ? w[0].toUpperCase() + w.slice(1) : w));
  return shared.length ? shared.join(" ") : null;
}

/**
 * Clean a model's name suggestion down to a short label, or null when it is unusable.
 *
 * @param {unknown} raw
 * @returns {string|null}
 */
export function cleanStackName(raw) {
  if (typeof raw !== "string") return null;
  const line = raw.split("\n").find((l) => l.trim()) || "";
  const name = line
    .replace(/^["'*\s]+|["'*.\s]+$/g, "")
    .replace(/^name:\s*/i, "")
    .trim();
  const words = name.split(/\s+/).filter(Boolean);
  if (!name || words.length > 3 || name.length > 40) return null;
  return name;
}
